import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Formik } from 'formik';

import {
  createTicketInitialValues,
  createTicketValidationSchema,
  ICreateTicketInitialValues,
} from '../form';
import { ThunkCreateTicket } from '../../../store/tickets/thunks';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { UserRoleSelector } from '../../../store/user/selector';
import { selectClientPortalCompanies } from '../../../store/ticketTemplates/selectors';
import { ThunkClientPortalCompanies } from '../../../store/ticketTemplates/thunks/clientPortalCompanies';

import Input from '../../../components/Input/Input';
import Button from '../../../components/Button/Button';
import Select from '../../../components/Select';

import { LOCAL_STORAGE_PSA } from '../../../constants/storage';
import { Roles } from '../../../constants/roles';
import { TICKET } from '../../../router/routes';

import useSharedStyles from '../../../sharedStyles';
import useStyles from '../styles';

const CreateTicketForm: React.FC = () => {
  const [processing, setProcessing] = useState<boolean>(false);

  const companyOptions = useSelector(selectClientPortalCompanies);

  const dispatch = useAppDispatch();
  const sharedStyles = useSharedStyles();
  const classes = useStyles();
  const navigate = useNavigate();
  const role = useAppSelector(UserRoleSelector);

  const handleSubmit = useCallback(
    (values: ICreateTicketInitialValues) => {
      setProcessing(true);
      dispatch(ThunkCreateTicket({ payload: values }))
        .unwrap()
        .then(res => {
          navigate(TICKET.replace(':ticketId', res.data));
        })
        .finally(() => {
          setProcessing(false);
        });
    },
    [dispatch],
  );

  const isConnectWiseAndNotUser =
    localStorage.getItem(LOCAL_STORAGE_PSA) === 'Connectwise' && role !== Roles.User;

  useEffect(() => {
    if (isConnectWiseAndNotUser) {
      dispatch(ThunkClientPortalCompanies());
    }
  }, [dispatch]);

  return (
    <div>
      <Formik
        initialValues={createTicketInitialValues}
        onSubmit={handleSubmit}
        validationSchema={createTicketValidationSchema(
          Boolean(isConnectWiseAndNotUser && companyOptions.length),
        )}
      >
        {formik => (
          <form onSubmit={formik.handleSubmit} className={classes.form}>
            {isConnectWiseAndNotUser && Boolean(companyOptions.length) && (
              <Select
                onChange={formik.handleChange}
                value={formik.values.companyId}
                label="Select customer"
                name="companyId"
                options={companyOptions}
                error={Boolean(formik.touched.companyId && formik.errors.companyId)}
                helperText={formik.errors.companyId as string}
                onBlur={formik.handleBlur}
              />
            )}
            <Input label="Summary" name="title" formik={formik} disabled={processing} />
            <Input
              label="Description"
              name="description"
              formik={formik}
              multiline
              minRows={4}
              disabled={processing}
            />

            <div className={sharedStyles.dialogActions}>
              <Button text={'Create'} type="submit" disabled={processing} loading={processing} />
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};

export default CreateTicketForm;
